import { useRequestEvent } from '#imports'
import { appendResponseHeader, getRequestHost, getRequestProtocol } from 'h3'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'

export function useSession() {
  const $httpFetch = useHttpFetch()
  const event = useRequestEvent()

  async function getSessionCookie(accessToken: string) {
    // Forward the SSR request origin to /auth/gateway so badoom's
    // CrossDomainSessionMiddleware can detect cross-domain scenarios (e.g.
    // local.backmarket.fr) and add Domain={market.domain} to the session cookie.
    // Without this, the middleware skips domain rewriting because the http-v2
    // SSR middleware does not forward Referer/Origin to the backend.
    const headers: Record<string, string> = {
      authorization: `Bearer ${accessToken}`,
    }

    if (event) {
      const protocol = getRequestProtocol(event, { xForwardedProto: true })
      const host = getRequestHost(event, { xForwardedHost: true })
      headers.origin = `${protocol}://${host}`
    }

    return $httpFetch('/auth/gateway', {
      headers,
      onResponse(context) {
        // During SSR, forward the Set-Cookie headers from the /auth/gateway
        // response to the browser so the session cookie is stored client-side.
        // Also inject the sessionid into the current request's Cookie header
        // so subsequent SSR requests (e.g. fetchUser) carry the new session.
        if (!event) return

        const setCookies = context.response.headers.getSetCookie()

        for (const cookie of setCookies) {
          appendResponseHeader(event, 'set-cookie', cookie)

          if (cookie.startsWith('sessionid=')) {
            const sessionidPair = cookie.split(';')[0]
            const existing = event.node.req.headers.cookie ?? ''

            // Replace any existing sessionid=... in the cookie header instead
            // of appending, because Django picks the first sessionid value.
            const withoutSessionid = existing
              .split(';')
              .map((c) => c.trim())
              .filter((c) => !c.startsWith('sessionid='))
              .join('; ')

            event.node.req.headers.cookie = withoutSessionid
              ? `${withoutSessionid}; ${sessionidPair}`
              : sessionidPair
          }
        }
      },
    })
  }

  return { getSessionCookie }
}
